import { COMPLETELY_LOADED_DEFAULT, MANUAL_SORTING } from '@/lib/constants';
import { computed } from 'vue';
import { guid } from 'shared/lib/uuid';
import { initIdList } from '@/lib/sort';
import { parentAlignmentType } from 'shared/constants.json';

export default function useGoalTree(viewsService, viewParamsService, filterService, sort, goalSelector, goals, expandSvc, childrenKey) {
  const goalTreeUid = guid();

  let uidCounter = {};
  let hangingNodes = [];

  const filteredStateGoals = computed(() => enrichedGoals.value.reduce((res, next) => {
    if (!filterService.inFilter(next)) {
      return res;
    }

    res[next.uid] = next;
    return res;
  }, {}));

  const enrichedGoals = computed(() => goals.value
    .filter((g) => g.completelyLoaded.includes(COMPLETELY_LOADED_DEFAULT))
    .map((g) => ({
      ...g,
      parents: goalSelector.selectMultiple(g.parents.map((p) => p.uid)),
    })));

  const inFilter = (uid) => filteredStateGoals.value[uid] !== undefined;
  const getNodeIndex = (treePath) => ['root', ...treePath].join('_');

  const appendChildren = (parentTreePath) => (res, goal) => {
    if (parentTreePath.includes(goal.uid)) {
      return res;
    }

    const treePath = [...parentTreePath, goal.uid];
    const isInFilter = inFilter(goal.uid);
    const children = goalSelector.selectMultiple(goal.children.map((c) => c.uid))
      .filter((c) => c.completelyLoaded.includes(COMPLETELY_LOADED_DEFAULT));

    const sortedChildren = sort(
      children,
      [
        ...viewParamsService.order.value,
        {
          attr: MANUAL_SORTING,
          sorting: initIdList(goal.childrenSort.map(({ uid }) => (uid)), goal.children.map((c) => c.uid)).map((uid) => ({ uid })),
        },
      ],
    );
    const relevantToDirectParent = goal.relevantToDirectParent || sortedChildren.some((c) => inFilter(c.uid));
    const childrenWithMetadata = sortedChildren.map((child) => ({
      ...child,
      relevantToDirectParent,
      hasAncestorInFilter: goal.hasAncestorInFilter || isInFilter,
    }));
    const childrenRes = childrenWithMetadata.reduce(appendChildren(treePath), []);

    res.push({
      ...goal,
      treePath,
      relevantToDirectParent,
      inFilter: isInFilter,
      loadedChildren: childrenRes,
    });

    return res;
  };
  const parseNode = (node, index, nodesLength, level, parentPath) => {
    const nodeIndex = getNodeIndex([...parentPath, node.uid]);

    const childrenFullyLoaded = node.loadedChildren.length === node.children.length;
    const res = {
      guid: `${nodeIndex}-${goalTreeUid}`,
      inFilter: node.inFilter,
      [childrenKey]: [],
      index: nodeIndex,
      indentation: level,
      isLastItem: index === nodesLength - 1,
      isFirstItem: index === 0,
      treePath: [...parentPath, node.uid],
      parents: parentPath.length > 0 ? [parentPath[parentPath.length - 1]] : [],
      hasChildren: (!childrenFullyLoaded && node.children.length > 0) || (childrenFullyLoaded && node.calculatedChildren.length > 0),
      entity: { uid: node.uid },
    };

    if (!expandSvc.getExpand({ index: nodeIndex }).value) {
      return res;
    }

    return {
      ...res,
      [childrenKey]: addMetadata(node.calculatedChildren, level + 1, [...parentPath, node.uid]),
    };
  };

  const removeUidRedundancies = (nodes) => {
    const result = [];
    for (let i = 0; i < nodes.length; i++) {
      if (uidCounter[nodes[i].uid] === 1) {
        result.push(nodes[i]);
        continue;
      }
      uidCounter[nodes[i].uid] -= 1;
    }
    return result;
  };

  const addNode = (node, children) => {
    uidCounter[node.uid] = (uidCounter[node.uid] || 0) + 1;
    return {
      ...node,
      calculatedChildren: children,
    };
  };

  const removeNodesSingle = (res, node) => {
    const r = removeNodes(node.loadedChildren);
    if (!filterService.inBaseFilter(node)) {
      hangingNodes.push(...r);
      return res;
    }
    if (inFilter(node.uid)) {
      res.push(addNode(node, r));
      return res;
    }
    if (viewParamsService.applyFilterOnFirstLevelOnly.value && node.hasAncestorInFilter) {
      res.push(addNode({ ...node, inFilter: true }, r));
      return res;
    }
    if (r.length === 0) {
      return res;
    }
    if (viewParamsService.parentAlignment.value === parentAlignmentType.full) {
      res.push(addNode(node, r));
      return res;
    }

    if (node.hasAncestorInFilter) {
      res.push(addNode(node, r));
      return res;
    }

    if (viewParamsService.parentAlignment.value === parentAlignmentType.direct) {
      if (node.relevantToDirectParent) {
        res.push(addNode(node, r));
        return res;
      }
    }
    hangingNodes.push(...r);
    return res;
  };

  const removeNodes = (nodes) => nodes.reduce(removeNodesSingle, []);

  const addMetadata = (nodes, level, parentPath) => nodes.map((n, index) => parseNode(
    n,
    index,
    nodes.length,
    level,
    parentPath,
  ));

  const goalTree = computed(() => {
    const roots = enrichedGoals.value.filter((g) => g.parents.filter((p) => p.completelyLoaded.includes(COMPLETELY_LOADED_DEFAULT)).length === 0);

    const tree = roots.reduce(appendChildren([]), []);

    uidCounter = {};
    hangingNodes = [];
    let filteredTree = removeNodes(tree);
    filteredTree.push(...hangingNodes);
    filteredTree = removeUidRedundancies(filteredTree);

    const sortedTree = sort(filteredTree, [...viewParamsService.order.value, {
      attr: MANUAL_SORTING,
      sorting: initIdList(viewsService.currentView.value.goalSort.map(({ uid }) => (uid)), filteredTree.map((c) => c.uid)).map((uid) => ({ uid })),
    }]);

    return addMetadata(sortedTree, 0, []);
  });

  return { goalTree };
}
